import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Square, RefreshCw, Loader2, Check, X, Ban, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  surface: "var(--bc-panel, #09090B)",
  elev: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  gold: "#E4B23C",
  green: "#22FFA3",
  amber: "#F59E0B",
  red: "#EF4444",
};

const mono = { fontFamily: "'JetBrains Mono',monospace" };

const OUTCOME_META = {
  win: { label: "VINTO", color: C.green },
  lose: { label: "PERSO", color: C.red },
  pending: { label: "IN CORSO", color: C.muted },
  unparseable: { label: "DA VALUTARE", color: C.amber },
  void: { label: "ANNULLATO", color: C.muted },
  no_bet: { label: "NO BET", color: "#8B5CF6" },
};

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{
      background: C.elev, border: `1px solid ${C.border}`,
      padding: "0.6rem 0.8rem", minWidth: 105,
    }}>
      <div style={{ ...mono, fontSize: "0.55rem", letterSpacing: "0.14em", textTransform: "uppercase", color: C.muted }}>
        {label}
      </div>
      <div style={{ ...mono, fontSize: "1.15rem", fontWeight: 700, color: color || C.text, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

export default function MonoliteHistoryPanel({ token, onToast }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/monolite/history`, {
        params: { admin_token: token, limit: 200 },
      });
      setData(res.data);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Caricamento storico Monolite fallito", "error");
    } finally {
      setLoading(false);
    }
  }, [token, onToast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const setOutcome = async (fixtureId, outcome) => {
    setSavingId(fixtureId);
    try {
      await axios.patch(`${API}/admin/monolite/${fixtureId}/outcome`, {
        admin_token: token, outcome,
      });
      onToast?.(`Esito aggiornato: ${OUTCOME_META[outcome]?.label || outcome}`);
      await load();
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Aggiornamento esito fallito", "error");
    } finally {
      setSavingId(null);
    }
  };

  const s = data?.stats;
  const rows = data?.rows || [];

  return (
    <div data-testid="monolite-history-panel" style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${C.gold}`, padding: open ? "1.25rem" : "0.85rem 1.25rem",
      marginBottom: "1.5rem", transition: "padding 0.2s ease",
    }}>
      <div
        data-testid="monolite-history-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap", cursor: "pointer" }}
      >
        <Square size={13} strokeWidth={3} style={{ color: C.gold }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.85rem",
          letterSpacing: "0.15em", textTransform: "uppercase", color: C.text,
        }}>
          Storico Monolite
        </span>
        {s && (
          <span style={{ ...mono, fontSize: "0.6rem", color: C.muted, letterSpacing: "0.08em" }}>
            {s.picks} pick · {s.win}V/{s.lose}P{s.win_rate != null ? ` · ${s.win_rate}%` : ""}
          </span>
        )}
        {!open && !s && (
          <span style={{ ...mono, fontSize: "0.6rem", color: C.muted, letterSpacing: "0.1em" }}>
            verdetti unici · esiti · rendimento
          </span>
        )}
        <span style={{ marginLeft: "auto", color: C.muted, display: "inline-flex" }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </div>

      {open && (
      <>
      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", margin: "0.9rem 0 1rem", flexWrap: "wrap" }}>
        <span style={{ ...mono, fontSize: "0.6rem", color: C.muted, letterSpacing: "0.1em" }}>
          verdetti unici · esiti · rendimento
        </span>
        <button
          data-testid="monolite-history-refresh-btn"
          onClick={load}
          disabled={loading}
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "0.35rem",
            background: "transparent", border: `1px solid ${C.border}`, color: C.muted,
            padding: "0.35rem 0.7rem", cursor: loading ? "wait" : "pointer",
            ...mono, fontSize: "0.62rem", letterSpacing: "0.12em", textTransform: "uppercase",
          }}
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Aggiorna esiti
        </button>
      </div>

      {s && (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <Stat testid="monolite-stat-picks" label="Pick emesse" value={s.picks} color={C.gold} />
          <Stat testid="monolite-stat-nobet" label="No Bet" value={s.no_bets} color="#8B5CF6" />
          <Stat testid="monolite-stat-win" label="Vinte" value={s.win} color={C.green} />
          <Stat testid="monolite-stat-lose" label="Perse" value={s.lose} color={C.red} />
          <Stat testid="monolite-stat-winrate" label="Win rate"
                value={s.win_rate === null || s.win_rate === undefined ? "—" : `${s.win_rate}%`}
                color={s.win_rate >= 60 ? C.green : s.win_rate >= 45 ? C.amber : C.red} />
          <Stat testid="monolite-stat-roi" label="ROI"
                value={s.roi_pct === null || s.roi_pct === undefined ? "—" : `${s.roi_pct > 0 ? "+" : ""}${s.roi_pct}%`}
                color={(s.roi_pct || 0) > 0 ? C.green : (s.roi_pct || 0) < 0 ? C.red : C.muted} />
          <Stat testid="monolite-stat-profit" label="Profitto (unità)"
                value={s.profit_units === null || s.profit_units === undefined ? "—" : `${s.profit_units > 0 ? "+" : ""}${s.profit_units}`}
                color={(s.profit_units || 0) > 0 ? C.green : (s.profit_units || 0) < 0 ? C.red : C.muted} />
          <Stat testid="monolite-stat-conf" label="Affid. media" value={s.avg_confidence ?? "—"} />
          <Stat testid="monolite-stat-odds" label="Quota media" value={s.avg_min_odds ?? "—"} />
          <Stat testid="monolite-stat-pending" label="In corso" value={s.pending} color={C.muted} />
        </div>
      )}

      {rows.length === 0 && !loading && (
        <p data-testid="monolite-history-empty" style={{ ...mono, fontSize: "0.72rem", color: C.muted, margin: 0 }}>
          Nessun verdetto Monolite ancora generato. Lancialo dalla pagina di una partita.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
        {rows.map((r) => {
          const meta = OUTCOME_META[r.outcome] || OUTCOME_META.pending;
          return (
            <div
              key={r.fixture_id}
              data-testid={`monolite-history-row-${r.fixture_id}`}
              style={{
                display: "flex", alignItems: "center", gap: "0.8rem", flexWrap: "wrap",
                background: C.elev, border: `1px solid ${C.border}`,
                borderLeft: `2px solid ${meta.color}`, padding: "0.55rem 0.75rem",
              }}
            >
              <span style={{
                ...mono, fontSize: "0.58rem", letterSpacing: "0.1em",
                padding: "0.15rem 0.4rem", color: meta.color,
                border: `1px solid ${meta.color}55`, background: `${meta.color}12`,
                minWidth: 96, textAlign: "center",
              }}>
                {meta.label}
              </span>

              <div style={{ minWidth: 210, flex: "1 1 210px" }}>
                <div style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  color: C.text, fontSize: "0.82rem", lineHeight: 1.2,
                }}>
                  {r.home} — {r.away}
                </div>
                <div style={{ ...mono, fontSize: "0.58rem", color: C.muted }}>
                  {r.league} · {r.kick_off ? new Date(r.kick_off).toLocaleDateString("it-IT") : "—"}
                  {r.ft_score ? ` · FT ${r.ft_score}` : ""}
                </div>
              </div>

              <div style={{ minWidth: 190, flex: "1 1 190px" }}>
                <div style={{ ...mono, fontSize: "0.76rem", color: r.is_pick ? C.gold : C.muted }}>
                  {r.is_pick ? r.bet : "NO BET"}
                </div>
                {r.is_pick && (
                  <div style={{ ...mono, fontSize: "0.58rem", color: C.muted }}>
                    aff. {Math.round(r.confidence_score || 0)}/100 · prob {Number(r.probability || 0).toFixed(0)}% · quota min {Number(r.min_odds || 0).toFixed(2)}
                  </div>
                )}
              </div>

              {r.is_pick && (
                <div style={{ display: "flex", gap: "0.3rem", marginLeft: "auto" }}>
                  {savingId === r.fixture_id ? (
                    <Loader2 size={13} className="animate-spin" style={{ color: C.muted }} />
                  ) : (
                    <>
                      <button
                        data-testid={`monolite-mark-win-${r.fixture_id}`}
                        title="Segna come vinto"
                        onClick={() => setOutcome(r.fixture_id, "win")}
                        style={{
                          background: r.outcome === "win" ? `${C.green}22` : "transparent",
                          border: `1px solid ${r.outcome === "win" ? C.green : C.border}`,
                          color: r.outcome === "win" ? C.green : C.muted,
                          padding: "0.2rem 0.35rem", cursor: "pointer", lineHeight: 1,
                        }}
                      ><Check size={12} /></button>
                      <button
                        data-testid={`monolite-mark-lose-${r.fixture_id}`}
                        title="Segna come perso"
                        onClick={() => setOutcome(r.fixture_id, "lose")}
                        style={{
                          background: r.outcome === "lose" ? `${C.red}22` : "transparent",
                          border: `1px solid ${r.outcome === "lose" ? C.red : C.border}`,
                          color: r.outcome === "lose" ? C.red : C.muted,
                          padding: "0.2rem 0.35rem", cursor: "pointer", lineHeight: 1,
                        }}
                      ><X size={12} /></button>
                      <button
                        data-testid={`monolite-mark-void-${r.fixture_id}`}
                        title="Annulla / non conteggiare"
                        onClick={() => setOutcome(r.fixture_id, "void")}
                        style={{
                          background: r.outcome === "void" ? "rgba(255,255,255,0.06)" : "transparent",
                          border: `1px solid ${C.border}`, color: C.muted,
                          padding: "0.2rem 0.35rem", cursor: "pointer", lineHeight: 1,
                        }}
                      ><Ban size={12} /></button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}
